'use client';

import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { track } from '@/lib/telemetry';
import { localDayKey, transactionDate } from '@/lib/transactions/dates';
import {
  ArrowLeftIcon,
  ArrowUUpLeftIcon,
  BarcodeIcon,
  CalendarIcon,
  CaretRightIcon,
  CheckIcon,
  ClockCountdownIcon,
  CopyIcon,
  CreditCardIcon,
  FlagIcon,
  HashIcon,
  IdentificationCardIcon,
  BuildingsIcon,
  MapPinIcon,
  ProhibitIcon,
  QrCodeIcon,
  ReceiptIcon,
  TagIcon,
  UserIcon,
  WalletIcon,
  ArrowCounterClockwiseIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { SpaceSendAction, SpaceTransactionMarker } from '@/components/spaces/SpaceSendAction';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { CategoryTile } from '@/components/CategoryTile';
import { InlineNameEditor } from '@/components/transactions/InlineNameEditor';
import { TagPill } from '@/components/transactions/TagPill';
import type { RenameBatchPreview } from '@/lib/api';
import {
  type CategoryIndex,
  getCategoryMeta,
} from './categoryIndex';
import {
  canFlagExpectedRefund,
  getRefundState,
  hasActiveExpectedRefund,
  hasReportingAmount,
  isFlowNeutral,
  reportingAmount,
  refundLinkedNoteMessage,
  uncoveredAmount,
} from '@/lib/transactions/refunds';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { useTransactionDetails } from '@/hooks/queries/useTransactionDetails';
import { useCategories } from '@/hooks/queries/useCategories';
import { buildCategoryTree, resolveCategoryDetailTarget } from '@/lib/categories/tree';
import Link from '@/components/ui/Link';
import { categoryProvenanceCopy, provenanceRuleTarget } from './categoryProvenance';
import { FaturaAffiliationPill } from './FaturaAffiliationPill';
import { RefundPill } from './RefundPill';
import type { ScreenFatura, ScreenTransaction } from './types';
import { cn } from '@/lib/utils';
import { buildMerchantDetailDisplay } from './transactionDetailMerchant';
import {
  addressBelongsInEstablishment,
  buildGeoPresentation,
  type GeoPresentation,
} from './transactionDetailLocation';
import { isPlottable } from './transactionLocationMapModel';
import { platformLabel } from '@/lib/transactions/platform';
import { useFlagMerchant } from '@/hooks/queries/useFlagMerchant';

// echarts + the 38KB UF outline are worth ~1MB in the chunk this drawer lives in, and the
// map renders for a minority of transactions (both cities known AND different). Loaded on
// demand per the repo's code-splitting convention.
const TransactionLocationMap = lazy(() =>
  import('./TransactionLocationMap').then((m) => ({ default: m.TransactionLocationMap })),
);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tx: ScreenTransaction | null;
  faturaById?: ReadonlyMap<string, ScreenFatura>;
  onOpenFatura?: (faturaId: string) => void;
  categoryIndex: CategoryIndex;
  /** When provided, renders a back arrow in the hero (e.g. to return to the drawer this opened from). */
  onBack?: () => void;
  /** Refund-link actions — each renders only when its handler is provided. */
  onOpenCounterpart?: (counterpartTransactionId: string) => void;
  onOpenRefundLink?: (txId: string, mode: 'add' | 'relink') => void;
  onUnlinkRefund?: (tx: ScreenTransaction, linkId: string) => void;
  onFlagExpected?: (tx: ScreenTransaction, flagged: boolean) => void;
  isRefundSaving?: boolean;
  /** Renames (or, with null, restores) the display name (VER-377). */
  onRename?: (txId: string, name: string | null, applyBatch: boolean) => Promise<void> | void;
  /** Sizes the same-description rename batch (name-independent: population = description). */
  onPreviewRenameBatch?: (txId: string) => Promise<RenameBatchPreview>;
  isRenameSaving?: boolean;
  /** Arms the inline editor on open — the row's "clique para renomear" entry point. */
  startInRename?: boolean;
  /** Opens the tag assignment drawer (VER-378). */
  onEditTags?: (txId: string) => void;
}

const PAYMENT_LABELS: Record<string, string> = {
  pix: 'Pix',
  credit: 'Crédito',
  debit: 'Débito',
  transfer: 'Transferência',
  boleto: 'Boleto',
  other: 'Outros',
};

export function CopyableLine({ value, mono = false }: { value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          track('action.transaction.copy', { target: 'detail_field' });
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {});
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="group flex w-full min-w-0 cursor-pointer items-center gap-2 text-left"
    >
      <span className={cn('min-w-0 flex-1 break-all', mono && 'font-mono text-xs tabular-nums')}>
        {value}
      </span>
      <span
        aria-hidden="true"
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-text-tertiary group-hover:bg-surface-sunken group-hover:text-text-primary"
      >
        {copied ? (
          <CheckIcon className="size-3.5 text-success" weight="bold" />
        ) : (
          <CopyIcon className="size-3.5" weight="bold" />
        )}
      </span>
      <span className="sr-only">{copied ? 'Copiado' : 'Copiar'}</span>
    </button>
  );
}

function MetaRow({
  Icon,
  label,
  children,
}: {
  Icon: React.ComponentType<{ className?: string; weight?: 'duotone' }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="mt-0.5 size-4 shrink-0 text-text-tertiary" weight="duotone" />
      <div className="min-w-0 flex-1">
        <p className="text-2xs font-medium tracking-[0.15em] text-text-tertiary uppercase">
          {label}
        </p>
        <div className="mt-0.5 text-sm text-text-primary">{children}</div>
      </div>
    </div>
  );
}

/**
 * The "Local da compra" rows — every geo presentation state in one place (VER-358).
 *
 * The map FOLLOWS THE PURCHASE (#237's finding, re-confirmed by Davi 2026-08-16):
 * whenever the purchase city is plottable it renders as the map's single labelled point —
 * gating the map on the relation state alone made it almost unreachable in real data. The
 * city name lives as the map label, never additionally as text; the text line is only the
 * no-centroid fallback. A merchant's ADDRESS alone still never becomes a map: that would
 * fabricate a purchase location (those states return null here and render inside
 * "Estabelecimento").
 *
 * Owns the flag-merchant action too: the geo contradiction is the only surface that
 * offers it, so the hook lives with its one consumer.
 */
function LocationMetaRows({ geo, txId }: { geo: GeoPresentation; txId: string }) {
  const { flagMerchant, isFlagging, flaggedIds } = useFlagMerchant();

  if (geo.kind === 'same' || geo.kind === 'purchase-only' || geo.kind === 'split') {
    return (
      <MetaRow Icon={MapPinIcon} label="Local da compra">
        {isPlottable(geo.purchase) ? (
          <Suspense fallback={<div className="h-[200px]" aria-hidden />}>
            <TransactionLocationMap purchase={geo.purchase} />
          </Suspense>
        ) : (
          <p className="wrap-break-word">
            {geo.purchase.name} · {geo.purchase.uf}
          </p>
        )}
        {geo.kind === 'same' && (
          <p className="mt-1 text-xs text-text-secondary">
            Mesma cidade do CNPJ do estabelecimento.
          </p>
        )}
      </MetaRow>
    );
  }

  if (geo.kind !== 'relation') return null;

  // Both cities known and different: the relation IS the information, so the map is the
  // single presentation of both — the cities appear only as its labels. The footer keeps
  // what the drawing cannot say: the distance and what it means.
  return (
    <MetaRow Icon={MapPinIcon} label="Local da compra">
      <div
        className={cn(
          'overflow-hidden rounded-xl border',
          geo.warn ? 'border-warning-border bg-warning-surface' : 'border-border-subtle bg-surface-sunken',
        )}
      >
        <Suspense fallback={<div className="h-[200px]" aria-hidden />}>
          <TransactionLocationMap purchase={geo.purchase} address={geo.address} />
        </Suspense>
        <div className={cn('border-t px-2.5 py-2', geo.warn ? 'border-warning-border' : 'border-border-subtle')}>
          <p className={cn('text-xs', geo.warn ? 'text-warning-text' : 'text-text-secondary')}>
            <span className="font-mono tabular-nums">≈ {Math.round(geo.km)} km</span>
            {' · '}
            {geo.warn
              ? 'a compra e o CNPJ estão longe demais para serem o mesmo negócio, e o CNAE não vende no varejo. Provavelmente a identidade está errada.'
              : 'você comprou na filial; o CNPJ está registrado na matriz.'}
          </p>
          {geo.warn && (
            <button
              type="button"
              onClick={() => flagMerchant(txId)}
              disabled={isFlagging || flaggedIds.has(txId)}
              className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-warning-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FlagIcon className="size-3" weight="bold" />
              {flaggedIds.has(txId) ? 'Enviado — obrigado' : 'Isso parece errado'}
            </button>
          )}
        </div>
      </div>
    </MetaRow>
  );
}

function RefundActionPill({
  label,
  Icon,
  disabled,
  onClick,
}: {
  label: string;
  Icon: React.ComponentType<{ className?: string; weight?: 'bold' }>;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Icon className="size-3" weight="bold" />
      {label}
    </button>
  );
}

function RefundMetaSection({
  tx,
  onOpenCounterpart,
  onOpenRefundLink,
  onUnlinkRefund,
  onFlagExpected,
  saving,
}: {
  tx: ScreenTransaction;
  onOpenCounterpart?: (counterpartTransactionId: string) => void;
  onOpenRefundLink?: (txId: string, mode: 'add' | 'relink') => void;
  onUnlinkRefund?: (tx: ScreenTransaction, linkId: string) => void;
  onFlagExpected?: (tx: ScreenTransaction, flagged: boolean) => void;
  saving: boolean;
}) {
  const formatCurrency = useFormatCurrency();
  const state = getRefundState(tx);
  const link = tx.refundLink;
  const reportingAvailable = hasReportingAmount(tx);
  const baseNeutral = (tx.isCashFlowNeutral ?? tx.isInternalTransfer) === true && state === 'none';

  const handleOpenPicker = () => onOpenRefundLink?.(tx.id, 'add');
  const handleRelinkPicker = () => onOpenRefundLink?.(tx.id, 'relink');
  const handleFlag = () => onFlagExpected?.(tx, true);
  const handleUnflag = () => onFlagExpected?.(tx, false);

  // Transfers/fatura payments can never anchor a refund link.
  if (baseNeutral) return null;

  if (link) {
    // Both sides fan out: an estorno covers several parcelas, and an expense can be covered by
    // several partial estornos — so the counterpart list is used on either side.
    const counterparts = tx.refundLinks && tx.refundLinks.length > 0 ? tx.refundLinks : [link];
    const remainder = uncoveredAmount(tx);
    // Only an expense with value still uncovered can take a further estorno.
    const canAddAnother = reportingAvailable && onOpenRefundLink
      && link.role === 'expense' && remainder > 0;
    const flagStillActive = hasActiveExpectedRefund(tx);
    return (
      <MetaRow Icon={ArrowUUpLeftIcon} label={link.role === 'refund' ? 'Reembolso de' : 'Reembolsado por'}>
        <div className="space-y-2">
          {counterparts.map((cp) => {
            const counterpartSigned = cp.role === 'refund'
              ? -Math.abs(cp.counterpartAmount)
              : Math.abs(cp.counterpartAmount);
            const counterpartDate = new Date(cp.counterpartDate);
            const counterpartDayLabel = counterpartDate.toLocaleDateString('pt-BR', {
              day: '2-digit', month: 'short', timeZone: 'UTC',
            });
            const counterpartName = cp.counterpartMerchantName ?? cp.counterpartDescription;
            // Several partial estornos often share a description, so the date disambiguates
            // them for screen readers. (The amount is deliberately left out — it is masked by
            // PrivacyContext for sighted users and an aria-label would leak it.)
            //
            // A series link is NOT removable on its own: DeleteLinkAsync resolves the root and
            // drops the whole propagated series, so the control must say so rather than name
            // just this counterpart.
            const unlinkLabel = cp.isSeriesLink
              ? `Desvincular toda a série parcelada (inclui ${counterpartName} de ${counterpartDayLabel})`
              : `Desvincular ${counterpartName} de ${counterpartDayLabel}`;
            return (
              <div
                key={cp.counterpartTransactionId}
                className="flex items-center gap-1 rounded-xl border border-border bg-surface-sunken"
              >
                <button
                  type="button"
                  onClick={() => onOpenCounterpart?.(cp.counterpartTransactionId)}
                  disabled={!onOpenCounterpart}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl p-3 text-left hover:bg-accent disabled:cursor-default"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text-primary">
                      {cp.counterpartMerchantName ?? cp.counterpartDescription}
                    </span>
                    <span className="mt-0.5 block text-xs text-text-secondary">
                      {counterpartDayLabel}
                      {' · '}
                      {cp.counterpartAccountName}
                      {cp.isSeriesLink && ' · série parcelada'}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'shrink-0 font-mono text-sm tabular-nums',
                      counterpartSigned >= 0 ? 'text-amount-positive' : 'text-amount-negative',
                    )}
                  >
                    {counterpartSigned >= 0 ? '+' : ''}
                    <MoneyValue text={formatCurrency(counterpartSigned)} />
                  </span>
                  {onOpenCounterpart && (
                    <CaretRightIcon className="size-3.5 shrink-0 text-text-tertiary" weight="bold" aria-hidden />
                  )}
                </button>
                {/* Per-link unlink: with several counterparts a single button could not say
                    WHICH one to remove. Sibling of the open-counterpart button, never nested. */}
                {onUnlinkRefund && (
                  <button
                    type="button"
                    onClick={() => onUnlinkRefund(tx, cp.linkId)}
                    disabled={saving}
                    aria-label={unlinkLabel}
                    title={unlinkLabel}
                    className="mr-2 grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg text-text-tertiary hover:bg-accent hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <ProhibitIcon className="size-4" weight="bold" aria-hidden />
                  </button>
                )}
              </div>
            );
          })}
          {state === 'refunded-partial' && (
            <p className="text-xs text-text-secondary">
              Restante de{' '}
              <span className="font-mono tabular-nums">
                <MoneyValue text={formatCurrency(remainder)} />
              </span>{' '}
              segue no seu ritmo de gastos.
            </p>
          )}
          <p className="rounded-lg border border-info-border bg-info-surface px-2.5 py-1.5 text-xs text-info-text">
            {refundLinkedNoteMessage(tx)}
          </p>
          {/* A partly refunded expense can ALSO carry an active "previsto" flag for its
              uncovered remainder. Surfaced here because this branch owns the linked row —
              otherwise the link would hide the flag and its removal action entirely. */}
          {flagStillActive && (
            <p className="rounded-lg border border-warning-border bg-warning-surface px-2.5 py-1.5 text-xs text-warning-text">
              Reembolso previsto para o restante — enquanto a flag valer, a despesa inteira fica
              fora do seu ritmo de gastos.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {flagStillActive && onFlagExpected && (
              <RefundActionPill label="Remover flag" Icon={ClockCountdownIcon} disabled={saving} onClick={handleUnflag} />
            )}
            {/* A propagated series can't be re-pointed one link at a time (RelinkAsync 409s),
                so "Alterar vínculo" is hidden — only "Desvincular" (which cascades) is offered. */}
            {reportingAvailable && onOpenRefundLink && !link.isSeriesLink && counterparts.length === 1 && (
              <RefundActionPill label="Alterar vínculo" Icon={ArrowUUpLeftIcon} disabled={saving} onClick={handleRelinkPicker} />
            )}
            {canAddAnother && (
              <RefundActionPill label="Vincular outro reembolso" Icon={ArrowUUpLeftIcon} disabled={saving} onClick={handleOpenPicker} />
            )}
          </div>
        </div>
      </MetaRow>
    );
  }

  if (state === 'expected') {
    return (
      <MetaRow Icon={ClockCountdownIcon} label="Reembolso">
        <div className="space-y-2">
          <p className="rounded-lg border border-warning-border bg-warning-surface px-2.5 py-1.5 text-xs text-warning-text">
            {tx.paymentType === 'credit'
              ? 'Reembolso previsto — válido enquanto a fatura estiver aberta. Se ela fechar sem o estorno, a despesa volta ao seu ritmo de gastos.'
              : 'Reembolso previsto — válido por 90 dias. Se o estorno não chegar, a despesa volta ao seu ritmo de gastos.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {reportingAvailable && onOpenRefundLink && (
              <RefundActionPill label="Vincular estorno recebido" Icon={ArrowUUpLeftIcon} disabled={saving} onClick={handleOpenPicker} />
            )}
            {onFlagExpected && (
              <RefundActionPill label="Remover flag" Icon={ClockCountdownIcon} disabled={saving} onClick={handleUnflag} />
            )}
          </div>
        </div>
      </MetaRow>
    );
  }

  // A refund link is anchored on the EXPENSE that was refunded — you never start it from an
  // inflow. The estorno (income) side gets linked from the expense or via detection review, so
  // an unlinked income transaction offers no "Vincular reembolso".
  const reportingValue = reportingAmount(tx);
  const canLinkRefund = reportingAvailable && !!onOpenRefundLink
    && reportingValue != null && reportingValue < 0;
  const canFlag = reportingAvailable && !!onFlagExpected && canFlagExpectedRefund(tx);
  if (!canLinkRefund && !canFlag) return null;

  return (
    <MetaRow Icon={ArrowUUpLeftIcon} label="Reembolso">
      <div className="flex flex-wrap gap-2">
        {canLinkRefund && (
          <RefundActionPill label="Vincular reembolso" Icon={ArrowUUpLeftIcon} disabled={saving} onClick={handleOpenPicker} />
        )}
        {canFlag && (
          <RefundActionPill label="Reembolso previsto" Icon={ClockCountdownIcon} disabled={saving} onClick={handleFlag} />
        )}
      </div>
    </MetaRow>
  );
}

export function TransactionDetailDrawer({
  open,
  onOpenChange,
  tx,
  faturaById,
  onOpenFatura,
  categoryIndex,
  onBack,
  onOpenCounterpart,
  onOpenRefundLink,
  onUnlinkRefund,
  onFlagExpected,
  isRefundSaving = false,
  onRename,
  onPreviewRenameBatch,
  isRenameSaving = false,
  startInRename = false,
  onEditTags,
}: Props) {
  const formatCurrency = useFormatCurrency();
  // ── Rename (VER-377): clicking the NAME arms the inline editor. ──
  const [renaming, setRenaming] = useState(false);
  const [renameBatch, setRenameBatch] = useState(false);
  const [renamePreview, setRenamePreview] = useState<RenameBatchPreview | null>(null);
  const [renamePreviewLoading, setRenamePreviewLoading] = useState(false);
  const txId = tx?.id ?? null;
  useEffect(() => {
    // Re-arm per transaction: the row's "Renomear" entry opens pre-armed; a
    // counterpart swap or reopen resets to display mode.
    setRenaming(open && startInRename);
    setRenameBatch(false);
    setRenamePreview(null);
  }, [open, startInRename, txId]);
  // Population size is name-independent (matches = same description), so one fetch
  // when the batch checkbox arms is enough for the whole edit.
  useEffect(() => {
    if (!renaming || !renameBatch || !txId || !onPreviewRenameBatch) return;
    let alive = true;
    setRenamePreviewLoading(true);
    onPreviewRenameBatch(txId)
      .then((preview) => {
        if (alive) setRenamePreview(preview);
      })
      .catch(() => {
        if (alive) setRenamePreview(null);
      })
      .finally(() => {
        if (alive) setRenamePreviewLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [renaming, renameBatch, txId, onPreviewRenameBatch]);
  // Fetch details lazily — only when a tx is selected and the drawer is open.
  const { data: loadedDetails } = useTransactionDetails(open && tx ? tx.id : null);
  // Only to resolve a leaf category ref to its root for the rules link; the SWR key is
  // shared with every other taxonomy consumer, so this costs no extra request.
  const { data: taxonomy } = useCategories(open);
  const categoryTree = useMemo(() => buildCategoryTree(taxonomy), [taxonomy]);

  // TRANSACTION_SWR_OPTIONS sets keepPreviousData, and openCounterpartDetails swaps `tx`
  // in place without closing the drawer — so between the swap and the new response SWR
  // still holds the PREVIOUS transaction's payload. Pin it to the transaction on screen:
  // an empty row while the request is in flight is right; one transaction's merchant,
  // platform, purchase city or Pix counterparty shown under another is not.
  const details = loadedDetails?.id === tx?.id ? loadedDetails : undefined;

  const provenance = categoryProvenanceCopy(details?.categoryProvenance);
  // Only a rule can be opened, and only once the taxonomy is loaded: the hub is keyed by
  // ROOT, so a leaf ref has to be resolved before it can be linked to. Unresolvable ⇒ no
  // link rather than a dead one.
  const rulesHref = useMemo(() => {
    const target = provenanceRuleTarget(details?.categoryProvenance);
    if (!target) return null;
    // A ref is CUSTOM only on the transaction's own say-so. Inferring it from "absent
    // from the taxonomy" would call every Pluggy leaf custom while `useCategories` is
    // still loading and link to a leaf as though it were a root; a genuinely unknown ref
    // would get the same wrong treatment. Pluggy resolution returns null until the
    // taxonomy actually contains the ref, so the link appears when it can be right.
    const isCustom = details?.customCategoryId === target.categoryRef;
    const resolved = resolveCategoryDetailTarget(
      target.categoryRef,
      isCustom ? 'custom' : 'pluggy',
      categoryTree,
    );
    if (!resolved) return null;
    const params = new URLSearchParams({ catDetail: resolved.rootId, catDetailView: 'regras' });
    if (resolved.childId) params.set('catDetailSub', resolved.childId);
    return `/budget?${params.toString()}`;
  }, [details?.categoryProvenance, details?.customCategoryId, categoryTree]);

  if (!tx) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-tokens="v2">
          <DrawerHeader className="sr-only">
            <DrawerTitle>Transação</DrawerTitle>
            <DrawerDescription>Detalhes da transação selecionada.</DrawerDescription>
          </DrawerHeader>
        </DrawerContent>
      </Drawer>
    );
  }

  const cat = getCategoryMeta(categoryIndex, tx.categoryId);
  const positive = tx.amount >= 0;
  const displayMoney = tx.originalMoney ?? {
    amount: tx.amount,
    currencyCode: tx.currencyCode ?? 'BRL',
  };
  const refundState = getRefundState(tx);
  const neutral = isFlowNeutral(tx);
  const period = transactionDate(tx.date);
  const original = transactionDate(tx.source.date);
  // Same reading the two rows render, so the original date shows exactly when it reads differently.
  const showOriginalDate = localDayKey(tx.source.date) !== localDayKey(tx.date);
  const fatura = tx.faturaId && faturaById ? faturaById.get(tx.faturaId) : undefined;
  const account = tx.source.account;
  const accountLabel = account?.name ?? 'Conta';
  const bank = account?.item?.connectorName;
  const merchantDetails = buildMerchantDetailDisplay(details, tx.source, details?.establishment);
  const geo = buildGeoPresentation(details);
  const platform = details?.platform ?? null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-tokens="v2"
        className="flex flex-col bg-surface px-0 data-[vaul-drawer-direction=right]:h-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="px-5 pt-3 pb-0">
          <DrawerTitle className="sr-only">Detalhes da transação</DrawerTitle>
          <DrawerDescription className="sr-only">
            Detalhes da transação selecionada, incluindo conta, pagamento e comprovante.
          </DrawerDescription>
        </DrawerHeader>

        {/* Hero — category icon, merchant, amount */}
        <div className="px-5 pt-1 pb-4">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Voltar"
                className="-ml-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
              >
                <ArrowLeftIcon className="size-5" />
              </button>
            )}
            <CategoryTile meta={cat} size="lg" />
            <div className="min-w-0 flex-1">
              {renaming && onRename ? (
                <div className="min-w-0">
                  <InlineNameEditor
                    initialValue={tx.merchant}
                    saving={isRenameSaving}
                    // A batch may only commit against a settled preview that says it can
                    // run; unchecking the batch always re-enables the single rename.
                    commitDisabled={renameBatch
                      && (renamePreviewLoading || renamePreview == null || renamePreview.exceedsLimit)}
                    onCommit={(value) => {
                      void onRename(tx.id, value, renameBatch);
                      setRenaming(false);
                    }}
                    onCancel={() => setRenaming(false)}
                  />
                  <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs text-text-secondary">
                    <input
                      type="checkbox"
                      data-testid="rename-batch"
                      checked={renameBatch}
                      disabled={isRenameSaving}
                      onChange={(e) => {
                        setRenameBatch(e.target.checked);
                        // Optimistic: the fetch effect runs only after paint, and the
                        // gap would flash the failure copy before the first request.
                        if (e.target.checked) setRenamePreviewLoading(true);
                      }}
                      className="mt-px size-4 appearance-none rounded-sm border border-border-strong bg-surface checked:border-primary checked:bg-primary forced-colors:appearance-auto"
                    />
                    <span>
                      {renameBatch && renamePreview?.exceedsLimit
                        ? `Mais de ${renamePreview.limit} lançamentos com essa descrição. Desmarque para renomear só este.`
                        : renameBatch && renamePreviewLoading
                          ? 'Calculando os lançamentos com a mesma descrição...'
                          : renameBatch && renamePreview
                            ? `Renomear também os ${renamePreview.totalMatches} lançamentos com a mesma descrição`
                            : renameBatch
                              ? 'Não foi possível calcular o lote. Desmarque para renomear só este.'
                              : 'Renomear também os lançamentos com a mesma descrição'}
                    </span>
                  </label>
                </div>
              ) : onRename ? (
                /* The NAME is the click target (VER-377): PostHog shows dead clicks
                   landing on descriptor text — the visible pencil says "editable". */
                <button
                  type="button"
                  data-testid="detail-name"
                  title="Clique para renomear"
                  aria-label="Renomear descrição"
                  onClick={() => setRenaming(true)}
                  className="-mx-1.5 flex w-[calc(100%+0.75rem)] min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-surface-sunken"
                >
                  <span className="truncate text-base font-semibold text-text-primary">{tx.merchant}</span>
                  <PencilSimpleIcon className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
                </button>
              ) : (
                <p className="truncate text-base font-semibold text-text-primary">{tx.merchant}</p>
              )}
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-text-secondary">
                <span
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: cat.color }}
                  aria-hidden
                />
                <span className="truncate">{cat.label}</span>
              </p>
              {provenance && (
                <p className="mt-0.5 truncate text-2xs text-text-tertiary">
                  {provenance.lead}
                  {provenance.name &&
                    (rulesHref ? (
                      // The line becomes the UNDO path, not just an explanation — which
                      // matters most for a merchant rule, whose reach spans every
                      // descriptor variant of the identity.
                      <Link
                        href={rulesHref}
                        className="cursor-pointer font-medium text-text-secondary underline underline-offset-2"
                      >
                        {provenance.name}
                      </Link>
                    ) : (
                      <b className="font-medium text-text-secondary">{provenance.name}</b>
                    ))}
                </p>
              )}
              {/* "via …" — the channel THIS purchase went through (transactions.platform,
                  VER-358). The seller stays the merchant above; absent when no channel is
                  asserted, which is never "bought directly". */}
              {platform && (
                <span className="mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-2xs font-medium text-text-secondary">
                  <span className="truncate">via {platformLabel(platform)}</span>
                </span>
              )}
            </div>
          </div>

          <p
            className={cn(
              'mt-3 font-mono text-[1.75rem] leading-none tabular-nums',
              neutral ? 'text-text-tertiary' : positive ? 'text-amount-positive' : 'text-amount-negative',
            )}
          >
            {positive ? '+' : ''}
            {formatCurrency(displayMoney.amount, { currency: displayMoney.currencyCode })}
          </p>
          {tx.originalMoney && (
            <p className="mt-1 font-mono text-xs text-text-secondary tabular-nums">
              {formatCurrency(tx.amount, { currency: tx.currencyCode ?? 'BRL' })} lançado na conta
            </p>
          )}
          {tx.reportingStatus === 'conversion_unavailable' && (
            <p className="mt-1 text-xs text-warning-text">
              O banco ainda não informou a conversão para reais.
            </p>
          )}
          <RefundPill state={refundState} hero className="mt-2" />
          <SpaceTransactionMarker transactionId={tx.id} />
          <div className="mt-3"><SpaceSendAction transaction={tx.source} /></div>
        </div>

        {/* Meta list */}
        <div className="flex-1 divide-y divide-border-subtle overflow-y-auto px-5">
          <MetaRow Icon={CalendarIcon} label="Período">
            <span className="font-mono tabular-nums">
              {period.date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
              {period.hasTime ? ` · ${period.date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : null}
            </span>
          </MetaRow>

          {showOriginalDate && (
            <MetaRow Icon={CalendarIcon} label="Data original">
              <span className="font-mono tabular-nums">
                {original.date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                {original.hasTime ? ` · ${original.date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : null}
              </span>
            </MetaRow>
          )}

          <MetaRow Icon={WalletIcon} label="Conta">
            <span>
              {bank ? `${bank} · ` : ''}
              {accountLabel}
            </span>
          </MetaRow>

          <MetaRow Icon={CreditCardIcon} label="Forma de pagamento">
            <span>{PAYMENT_LABELS[tx.paymentType] ?? '—'}</span>
          </MetaRow>

          {merchantDetails && (
            <MetaRow Icon={TagIcon} label="Estabelecimento">
              <div className="space-y-0.5">
                <p className="font-medium wrap-break-word">{merchantDetails.name}</p>
                {merchantDetails.metadata.length > 0 && (
                  <p className="text-xs wrap-break-word text-text-secondary">
                    {merchantDetails.metadata.join(' · ')}
                  </p>
                )}
                {/* The company's registered seat — printed here ONLY when it was not
                    already presented as a map label or the "same city" line (one
                    presentation per fact, VER-358). Never phrased as where you bought. */}
                {addressBelongsInEstablishment(geo) && (
                  <p className="flex items-center gap-1 text-xs text-text-secondary">
                    <BuildingsIcon className="size-3 shrink-0" weight="duotone" />
                    {geo.address.name} · {geo.address.uf}
                    <span className="text-text-tertiary">(registro do CNPJ)</span>
                  </p>
                )}
              </div>
            </MetaRow>
          )}

          {/* Local da compra — every geo state, one presentation per fact (VER-358).
              Omitted entirely when the descriptor carried no city, which is most
              transactions: a "sem local" line would be permanent noise, and since VER-356
              platform markers rightly yield NO city, absence also keeps a platform's own
              city from reading as where the user bought. */}
          <LocationMetaRows geo={geo} txId={tx.id} />

          {tx.installment && (
            <MetaRow Icon={HashIcon} label="Parcela">
              <span className="font-mono tabular-nums">
                {tx.installment.current}/{tx.installment.total}
              </span>
            </MetaRow>
          )}

          {fatura && onOpenFatura && (
            <MetaRow Icon={ReceiptIcon} label="Fatura">
              <FaturaAffiliationPill fatura={fatura} onOpen={onOpenFatura} />
            </MetaRow>
          )}

          {onEditTags && (
            <MetaRow Icon={HashIcon} label="Tags">
              <div data-testid="detail-tags-row" className="flex flex-wrap items-center gap-1.5">
                {(tx.tags ?? []).map((tag) => (
                  <TagPill key={tag.id} tag={tag} />
                ))}
                {(tx.tags?.length ?? 0) === 0 && (
                  <span className="text-text-tertiary">Sem tags</span>
                )}
                <button
                  type="button"
                  data-testid="detail-tags-edit"
                  onClick={() => onEditTags(tx.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken"
                >
                  <PencilSimpleIcon className="size-3" weight="bold" />
                  {(tx.tags?.length ?? 0) > 0 ? 'Editar' : 'Adicionar'}
                </button>
              </div>
            </MetaRow>
          )}

          <RefundMetaSection
            tx={tx}
            onOpenCounterpart={onOpenCounterpart}
            onOpenRefundLink={onOpenRefundLink}
            onUnlinkRefund={onUnlinkRefund}
            onFlagExpected={onFlagExpected}
            saving={isRefundSaving}
          />

          {/* The bank's own naming never disappears (VER-377): a renamed row shows the
              pre-rename description here, with the raw descriptor beneath when it
              differs — and the restore action lives with the fact it restores. */}
          {(tx.renamed || (tx.source.descriptionRaw && tx.source.descriptionRaw !== tx.merchant)) && (
            <MetaRow Icon={TagIcon} label="Descrição original">
              <div className="space-y-1">
                {tx.renamed && (
                  <span data-testid="detail-original-desc" className="block wrap-break-word text-text-secondary">
                    {tx.source.description}
                  </span>
                )}
                {tx.source.descriptionRaw
                  && tx.source.descriptionRaw !== tx.merchant
                  && tx.source.descriptionRaw !== tx.source.description && (
                  <span className="block wrap-break-word text-text-secondary">{tx.source.descriptionRaw}</span>
                )}
                {tx.renamed && onRename && (
                  <button
                    type="button"
                    data-testid="restore-name"
                    onClick={() => void onRename(tx.id, null, false)}
                    disabled={isRenameSaving}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:opacity-60"
                  >
                    <ArrowCounterClockwiseIcon className="size-3" weight="bold" />
                    Restaurar nome original
                  </button>
                )}
              </div>
            </MetaRow>
          )}

          {tx.notes && (
            <MetaRow Icon={TagIcon} label="Notas">
              <span className="wrap-break-word whitespace-pre-wrap">{tx.notes}</span>
            </MetaRow>
          )}

          {/* Pix / TED — counterparty + reference + reason */}
          {details?.receipt?.counterparty && (
            <MetaRow Icon={UserIcon} label="Contraparte">
              <div className="space-y-0.5">
                {details.receipt.counterparty.name && (
                  <p className="font-medium">{details.receipt.counterparty.name}</p>
                )}
                {(details.receipt.counterparty.document || details.receipt.counterparty.documentLast4) && (
                  <p className="font-mono text-xs text-text-secondary tabular-nums">
                    {details.receipt.counterparty.documentType ?? 'Doc'}
                    {' '}
                    {details.receipt.counterparty.document
                      ?? `····${details.receipt.counterparty.documentLast4}`}
                  </p>
                )}
                {details.receipt.counterparty.bank && (
                  <p className="text-xs text-text-secondary">{details.receipt.counterparty.bank}</p>
                )}
              </div>
            </MetaRow>
          )}

          {details?.pix?.keyType && (
            <MetaRow Icon={QrCodeIcon} label="Chave Pix">
              <span className="text-2xs tracking-wider uppercase">{details.pix.keyType}</span>
            </MetaRow>
          )}

          {details?.receipt?.referenceNumber && (
            <MetaRow Icon={IdentificationCardIcon} label="ID da transação">
              <CopyableLine value={details.receipt.referenceNumber} />
            </MetaRow>
          )}

          {details?.receipt?.reason && (
            <MetaRow Icon={TagIcon} label="Descrição">
              <span className="wrap-break-word whitespace-pre-wrap text-text-secondary">{details.receipt.reason}</span>
            </MetaRow>
          )}

          {/* Boleto */}
          {details?.boleto && (
            <>
              {(details.boleto.penaltyAmount || details.boleto.interestAmount || details.boleto.discountAmount) && (
                <MetaRow Icon={ReceiptIcon} label="Encargos">
                  <div className="space-y-0.5 text-xs">
                    {details.boleto.baseAmount !== undefined && (
                      <p>Valor do boleto: <span className="font-mono tabular-nums">{formatCurrency(details.boleto.baseAmount)}</span></p>
                    )}
                    {details.boleto.penaltyAmount ? (
                      <p>Multa: <span className="font-mono tabular-nums">{formatCurrency(details.boleto.penaltyAmount)}</span></p>
                    ) : null}
                    {details.boleto.interestAmount ? (
                      <p>Juros: <span className="font-mono tabular-nums">{formatCurrency(details.boleto.interestAmount)}</span></p>
                    ) : null}
                    {details.boleto.discountAmount ? (
                      <p>Desconto: <span className="font-mono tabular-nums">-{formatCurrency(details.boleto.discountAmount)}</span></p>
                    ) : null}
                  </div>
                </MetaRow>
              )}
              {details.boleto.digitableLine && (
                <MetaRow Icon={BarcodeIcon} label="Linha digitável">
                  <CopyableLine value={details.boleto.digitableLine} mono />
                </MetaRow>
              )}
              {details.boleto.barcode && !details.boleto.digitableLine && (
                <MetaRow Icon={BarcodeIcon} label="Código de barras">
                  <CopyableLine value={details.boleto.barcode} mono />
                </MetaRow>
              )}
            </>
          )}

          <MetaRow Icon={HashIcon} label="ID interno">
            <CopyableLine value={tx.id} mono />
          </MetaRow>
        </div>

      </DrawerContent>
    </Drawer>
  );
}
